/**
 * Program-wide partner assumptions (Brief 1). Defaults match legacy constants;
 * admins edit via partner_program_settings. Engine and ledger code should read
 * resolved assumptions, not hard-coded literals.
 */
import type { PartnerProgramSettings } from "./schema";

export const PARTNER_PROGRAM_ASSUMPTION_DEFAULTS = {
  silverTrailingFundedCount: 5,
  goldTrailingFundedCount: 10,
  trailingStatusMonths: 24,
  silverUpliftBps: 1_000,
  goldUpliftBps: 2_000,
  upfrontCapBps: 5_000,
  silverAnniversaryMinFunded: 3,
  goldAnniversaryMinFunded: 5,
  paceBonusCents: 2_500_000,
  paceBonusFundingCount: 10,
  paceBonusWindowMonths: 12,
  rebateCapCents: 2_000_000,
  netAdminApprovalFloorCents: 6_000_000,
  upfrontPayBusinessDays: 15,
  backendPayDays: 30,
  introductionProtectionMonths: 12,
  nonCircumventionMonths: 24,
  terminationNoticeDays: 30,
  tailMonths: 12,
  leadTerminationNoticeDays: 30,
  leadPayDayOfMonth: 15,
} as const;

export type PartnerProgramAssumptions = {
  [K in keyof typeof PARTNER_PROGRAM_ASSUMPTION_DEFAULTS]: number;
};

type SettingsSlice = Partial<PartnerProgramSettings>;

export function partnerProgramAssumptionsFromSettings(
  settings: SettingsSlice | null | undefined,
): PartnerProgramAssumptions {
  const row = settings ?? {};
  const d = PARTNER_PROGRAM_ASSUMPTION_DEFAULTS;
  return {
    silverTrailingFundedCount: row.silverTrailingFundedCount ?? d.silverTrailingFundedCount,
    goldTrailingFundedCount: row.goldTrailingFundedCount ?? d.goldTrailingFundedCount,
    trailingStatusMonths: row.trailingStatusMonths ?? d.trailingStatusMonths,
    silverUpliftBps: row.silverUpliftBps ?? d.silverUpliftBps,
    goldUpliftBps: row.goldUpliftBps ?? d.goldUpliftBps,
    upfrontCapBps: row.upfrontCapBps ?? d.upfrontCapBps,
    silverAnniversaryMinFunded: row.silverAnniversaryMinFunded ?? d.silverAnniversaryMinFunded,
    goldAnniversaryMinFunded: row.goldAnniversaryMinFunded ?? d.goldAnniversaryMinFunded,
    paceBonusCents: row.paceBonusCents ?? d.paceBonusCents,
    paceBonusFundingCount: row.paceBonusFundingCount ?? d.paceBonusFundingCount,
    paceBonusWindowMonths: row.paceBonusWindowMonths ?? d.paceBonusWindowMonths,
    rebateCapCents: row.rebateCapCents ?? d.rebateCapCents,
    netAdminApprovalFloorCents: row.netAdminApprovalFloorCents ?? d.netAdminApprovalFloorCents,
    upfrontPayBusinessDays: row.upfrontPayBusinessDays ?? d.upfrontPayBusinessDays,
    backendPayDays: row.backendPayDays ?? d.backendPayDays,
    introductionProtectionMonths: row.introductionProtectionMonths ?? d.introductionProtectionMonths,
    nonCircumventionMonths: row.nonCircumventionMonths ?? d.nonCircumventionMonths,
    terminationNoticeDays: row.terminationNoticeDays ?? d.terminationNoticeDays,
    tailMonths: row.tailMonths ?? d.tailMonths,
    leadTerminationNoticeDays: row.leadTerminationNoticeDays ?? d.leadTerminationNoticeDays,
    leadPayDayOfMonth: row.leadPayDayOfMonth ?? d.leadPayDayOfMonth,
  };
}

/** Display-only catalog for the admin Partner Programs page. */
export const PARTNER_PROGRAM_CATALOG = {
  programs: [
    {
      id: "partner_program",
      label: "Partner Program",
      schedule: "Schedule A",
      audience: "Non-U.S. promoters",
      compensation: "Per project — package menu (upfront at I-526E receipt, optional back-end from loan interest)",
    },
    {
      id: "qualified_lead",
      label: "Qualified Lead Program",
      schedule: "Schedule B",
      audience: "U.S. partners who are not broker-dealers",
      compensation: "Flat fee per qualified lead, regardless of investment",
    },
  ],
  countryDefaultRule:
    "Non-U.S. country at onboarding → Partner Program. U.S. → Qualified Lead. Override requires admin reason and is logged.",
  brokerDealerRule: "Broker-dealer organizations and partners are blocked from both programs.",
  organizationTypes: [
    { organizationType: "law_firm", label: "Law firm", portalPersona: "Immigration Attorney" },
    { organizationType: "foreign_agent", label: "Foreign agent", portalPersona: "Foreign Migration Agent" },
    { organizationType: "broker_dealer", label: "Broker-dealer", portalPersona: "Broker-Dealer (blocked)" },
    { organizationType: "marketing", label: "Marketing", portalPersona: "Marketing Agent" },
    { organizationType: "wealth_manager", label: "Wealth manager", portalPersona: "Agent" },
    { organizationType: "other", label: "Other", portalPersona: "Agent" },
  ],
  packageMenuNote:
    "Per-project package menus (American Energy A/B/C, unit amount, admin fee, loan rate) are configured on each project's Deal Terms page, not here.",
} as const;
